import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CardMinimalist from "./CardMinimalist";

const ArticleCarousel = ({ shopId }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [articlesPerSlide] = useState(4);

  // Fonction pour mélanger un tableau (algorithme Fisher-Yates)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Récupérer les articles de la boutique spécifique
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://market.aiglesend.com/api/articles/getAllArticlesByShopId/${shopId}`
        );

        if (!response.ok) {
          throw new Error("Erreur lors de la récupération des articles");
        }

        const data = await response.json();

        // Mélanger les articles pour un affichage aléatoire
        const shuffledArticles = shuffleArray(data);
        setArticles(shuffledArticles); // Afficher TOUS les articles sans limitation
      } catch (error) {
        console.error("Erreur lors de la récupération des articles:", error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [shopId]);

  // Calculer le nombre total de slides
  const totalSlides = Math.ceil(articles.length / articlesPerSlide);

  // Naviguer vers la slide suivante
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  // Naviguer vers la slide précédente
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Aller à une slide spécifique
  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  // Obtenir les articles pour la slide actuelle
  const getCurrentSlideArticles = () => {
    const startIndex = currentSlide * articlesPerSlide;
    const endIndex = startIndex + articlesPerSlide;
    return articles.slice(startIndex, endIndex);
  };

  // Auto-play du carrousel (optionnel)
  useEffect(() => {
    if (articles.length > articlesPerSlide) {
      const interval = setInterval(() => {
        nextSlide();
      }, 5000); // Change de slide toutes les 5 secondes

      return () => clearInterval(interval);
    }
  }, [articles.length, currentSlide, totalSlides]);

  if (loading) {
    return (
      <div className="product-minimal">
        <div className="product-showcase">
          <h2 className="title">Découvrez nos plats à commander</h2>
          <div className="w-full text-center py-8">
            <p className="text-gray-500">Chargement des articles...</p>
          </div>
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="product-minimal">
        <div className="product-showcase">
          <h2 className="title">Découvrez nos plats à commander</h2>
          <div className="w-full text-center py-8">
            <p className="text-gray-500">
              Aucun article disponible pour cette boutique
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-minimal">
      <div className="product-showcase">
        <h2 className="title">Découvrez nos plats à commander</h2>

        <div className="carousel-container relative">
          {/* Boutons de navigation */}
          {totalSlides > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-orange-400 shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors"
                aria-label="Slide précédente"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>

              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-orange-400 shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors"
                aria-label="Slide suivante"
              >
                <ChevronRight size={24} className="text-white" />
              </button>
            </>
          )}

          {/* Contenu du carrousel */}
          <div className="carousel-wrapper overflow-hidden">
            <div
              className="carousel-track flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => {
                const slideArticles = articles.slice(
                  slideIndex * articlesPerSlide,
                  (slideIndex + 1) * articlesPerSlide
                );

                return (
                  <div
                    key={slideIndex}
                    className="carousel-slide w-full flex-shrink-0"
                  >
                    <div className="showcase-container grid grid-cols-2 grid-rows-2 gap-4 justify-items-center">
                      {slideArticles.map((article) => (
                        <div
                          className="w-full showcase-item-container"
                          key={article._id}
                        >
                          <CardMinimalist article={article} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Indicateurs de slides */}
          {totalSlides > 1 && (
            <div className="flex justify-center mt-6 space-x-2">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentSlide
                      ? "bg-orange-400"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Aller à la slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Compteur de slides */}
          {/*
          totalSlides > 1 && (
            <div className="text-center mt-4 text-sm text-gray-500">
              Slide {currentSlide + 1} sur {totalSlides}
            </div>
          ) */}
        </div>
      </div>
    </div>
  );
};

export default ArticleCarousel;
